import { useEffect, useState } from "react";
import superjson from "superjson";
import { appId, secureLocalStorage } from "~/src/appConfig";

export type LocalStateKey = {
  id: string;
} & Record<string, unknown>;

export const prefixKey = (key: LocalStateKey) => {
  const appPrefixedKey = { ...key, app: appId };
  return superjson.stringify(appPrefixedKey);
};

export type SecureLocalStorage = typeof secureLocalStorage;
const useLocalState = <T,>(key: LocalStateKey, defaultValue?: T, encrypted: boolean = false) => {
  const storage: Storage | SecureLocalStorage = encrypted ? secureLocalStorage : localStorage;
  const keyAsStr = prefixKey(key);

  const getValue = () => {
    const storedValue = storage.getItem(keyAsStr);
    if (storedValue !== null) {
      try {
        return superjson.parse(storedValue.toString()) as T;
      } catch (e) {
        console.error("Failed to parse stored value:", e);
        return defaultValue;
      }
    }
    if (defaultValue) {
      storage.setItem(keyAsStr, superjson.stringify(defaultValue));
    }
    return defaultValue;
  };

  // State to hold the current value
  const [value, setValue] = useState<T | undefined>(getValue);

  // Setter function to update the value in localStorage and state
  const setLocalState = (newValue: T) => {
    try {
      if (newValue === null) {
        storage.removeItem(keyAsStr);
        setValue(defaultValue);
      } else {
        storage.setItem(keyAsStr, superjson.stringify(newValue));
        setValue(newValue);
      }
    } catch (e) {
      console.error("Failed to set value in storage:", e);
    }
  };

  const clear = () => {
    storage.removeItem(keyAsStr);
  };

  // Effect to update the state when the key changes
  useEffect(() => {
    setValue(getValue());
  }, [keyAsStr]);

  const reload = () => {
    // sett data fra storage -> dispatch
    // TODO: bytt til zustand
    const storedValue = getValue();
    if (value != storedValue) {
      setValue(storedValue);
    }
  };

  // Lytter på endringer i localstorage
  useEffect(() => {
    window.addEventListener("storage", () => {
      reload();
    });
    return () => {
      window.removeEventListener("storage", () => {
        reload();
      });
    };
  }, [value]);

  return [value, setLocalState, clear, reload] as const;
};

export type LocalStateful<T> = {
  value: T | undefined;
  set: (value: T) => void;
  clear: () => void;
  reload: () => void;
};

export function useLocalStateful<T>(
  key: LocalStateKey,
  defaultValue?: T,
  encrypted: boolean = false,
): LocalStateful<T> {
  const [value, setValue, clear, reload] = useLocalState<T>(key, defaultValue, encrypted);
  return { value: value, set: setValue, clear: clear, reload: reload };
}

export default useLocalState;
